import React from 'react';
import { Button, Stack, Divider, alpha, useTheme } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventIcon from '@mui/icons-material/Event';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EmailIcon from '@mui/icons-material/Email';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { ROUTES } from '../../../constants/routes';
import { ROLES, FEATURES } from '../../../constants';

/**
 * NavItems - Main navigation menu items for desktop view
 * Displays navigation buttons with active state indicators
 *
 * @param {Object} currentUser - The currently authenticated user
 */
const NavItems = ({ currentUser }) => {
  const theme = useTheme();
  const location = useLocation();

  const isActive = (path) => {
    if (path === ROUTES.HOME) {
      return location.pathname === ROUTES.HOME;
    }
    return location.pathname.startsWith(path);
  };

  // Build navigation items with role-based grouping
  const isOfficeOrAdmin = currentUser?.role === ROLES.ADMIN || currentUser?.role === ROLES.OFFICE || currentUser?.role === ROLES.DEV;
  const isAdmin = currentUser?.role === ROLES.ADMIN || currentUser?.role === ROLES.DEV;

  const mainNavItems = currentUser ? [
    // MEINE ARBEIT / KOORDINATION section
    { text: 'Dashboard', path: ROUTES.DASHBOARD, icon: <DashboardIcon /> },
    { text: 'Kalender', path: ROUTES.CALENDAR, icon: <EventIcon /> },
    { text: 'Arbeitsstunden', path: ROUTES.WORK_HOURS, icon: <AccessTimeIcon /> },
    { text: 'Urlaub', path: ROUTES.TIME_OFF, icon: <BeachAccessIcon /> },
    { text: 'Terminberichte', path: ROUTES.REPORTS, icon: <DescriptionIcon /> },

    // Divider before KUNDEN & VERWALTUNG section
    { type: 'divider' },

    // KUNDEN & VERWALTUNG section
    { text: 'Kunden', path: ROUTES.CLIENTS, icon: <ArticleIcon /> },
    ...(isOfficeOrAdmin ? [
      { text: 'Rechnungen', path: ROUTES.INVOICES, icon: <ReceiptIcon /> },
      ...(FEATURES.EMAIL ? [{ text: 'E-Mails', path: ROUTES.EMAILS, icon: <EmailIcon /> }] : [])
    ] : []),

    // Divider before SYSTEM section (admin only)
    ...(isAdmin ? [{ type: 'divider' }] : []),

    // SYSTEM section (admin only)
    ...(isAdmin ? [
      { text: 'Admin', path: ROUTES.ADMIN_PANEL, icon: <AdminPanelSettingsIcon /> }
    ] : []),
  ] : [];

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        justifyContent: 'center',
        display: { xs: 'none', md: 'flex' },
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {mainNavItems.map((item, index) => {
        // Render divider
        if (item.type === 'divider') {
          return (
            <Divider
              key={`divider-${index}`}
              orientation="vertical"
              flexItem
              sx={{
                mx: 1,
                borderColor: alpha(theme.palette.common.white, 0.2),
                alignSelf: 'stretch',
              }}
            />
          );
        }

        // Render navigation button
        return (
          <motion.div
            key={item.path}
            whileHover={{ scale: 1.05 }}
            whileFocus={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <Button
              component={Link}
              to={item.path}
              startIcon={item.icon}
              sx={{
                color: theme.palette.common.white,
                fontWeight: 500,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                minWidth: 'auto',
                backgroundColor: isActive(item.path) ?
                  alpha(theme.palette.common.white, 0.15) :
                  'transparent',
                '&::after': isActive(item.path) ? {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '40%',
                  height: '3px',
                  backgroundColor: theme.palette.common.white,
                  borderRadius: '3px 3px 0 0',
                } : {},
                '&:hover': {
                  backgroundColor: alpha(theme.palette.common.white, 0.15),
                },
              }}
            >
              {item.text}
            </Button>
          </motion.div>
        );
      })}
    </Stack>
  );
};

export default NavItems;
